import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * Attaching a category to one wedding's board (PRD §F2 follow-up).
 *
 * `GET /api/categories` is the planner's vocabulary across every wedding; a
 * board is one wedding's subset of it. So this dialog does two things and
 * nothing else: offer an unattached master category, or mint a new one by
 * name when nothing on the master list fits.
 */

interface MasterCategory {
  id: string;
  name: string;
  sortOrder: number;
  archivedAt: string | null;
}

export default function AddCategoryDialog({
  weddingId,
  onBoard,
  open,
  onOpenChange,
}: {
  weddingId: string;
  onBoard: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return (await res.json()) as MasterCategory[];
    },
    enabled: open,
  });

  // Reopening after a cancel (or a prior error) must not carry over the last
  // search or a stale error message.
  useEffect(() => {
    if (open) {
      setSearch('');
      setError(null);
    }
  }, [open]);

  // The backend's uniqueness check is case-sensitive (so "Florist" and
  // "florist" could both exist in the master list), but a planner who already
  // has "Florist" on the board and types "florist" is not asking for a second
  // one — comparing case-insensitively here is what keeps that duplicate off
  // the list of options.
  const onBoardLower = onBoard.map((name) => name.toLowerCase());
  const available = (categories ?? []).filter(
    (c) => !onBoardLower.includes(c.name.toLowerCase()),
  );
  const searchLower = search.trim().toLowerCase();
  const visible = available.filter((c) => c.name.toLowerCase().includes(searchLower));
  const offerCreate = search.trim().length > 0 && visible.length === 0;

  const add = useMutation({
    mutationFn: async (payload: { categoryId: string } | { name: string }) => {
      const res = await client.api.weddings[':id'].categories.$post({
        param: { id: weddingId },
        json: payload,
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to add this category');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wedding', weddingId] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onOpenChange(false);
    },
    // Stays open on failure — a 409 (already attached) or 404 (deleted out
    // from under the planner mid-dialog) is something to react to, not a
    // reason to lose the search they just typed.
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add category</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            autoFocus
            placeholder="Search or name a new category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="max-h-64 space-y-1 overflow-y-auto">
            {visible.map((c) => (
              <Button
                key={c.id}
                type="button"
                variant="outline"
                className="w-full justify-start"
                disabled={add.isPending}
                onClick={() => add.mutate({ categoryId: c.id })}
              >
                {c.name}
              </Button>
            ))}

            {offerCreate && (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start"
                disabled={add.isPending}
                onClick={() => add.mutate({ name: search.trim() })}
              >
                Create "{search.trim()}"
              </Button>
            )}

            {!offerCreate && visible.length === 0 && (
              <p className="px-1 py-2 text-xs text-muted-foreground">
                Every category on your list is already on this board.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
